import { Component, ReactNode, ErrorInfo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorFallback } from './fallback-ui';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface VaultErrorFallbackProps {
  error?: Error;
  onRetry?: () => void;
  onNavigateHome?: () => void;
}

const VaultErrorFallback = ({ error, onRetry, onNavigateHome }: VaultErrorFallbackProps) => {
  return (
    <ErrorFallback
      error={error}
      onRetry={onRetry}
      onNavigateHome={onNavigateHome}
      title="Vault Error"
      description="There was an issue loading this vault. You can try again or return to the home page to access other vaults."
    />
  );
};

// Vault-level Error Boundary
class VaultErrorBoundaryClass extends Component<
  { 
    children: ReactNode; 
    vaultId?: string;
    vaultName?: string;
    onNavigateHome?: () => void;
  },
  ErrorBoundaryState
> {
  constructor(props: { 
    children: ReactNode; 
    vaultId?: string;
    vaultName?: string;
    onNavigateHome?: () => void;
  }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Vault Error Boundary caught an error:', error, errorInfo);
    console.error('Vault ID:', this.props.vaultId);
    console.error('Vault Name:', this.props.vaultName);
    // Here you could send error to logging service with vault context
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <VaultErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          onNavigateHome={this.props.onNavigateHome}
        />
      );
    }

    return this.props.children;
  }
}

// Functional wrapper for VaultErrorBoundary to use hooks
export const VaultErrorBoundary = ({ 
  children, 
  vaultId, 
  vaultName 
}: { 
  children: ReactNode; 
  vaultId?: string;
  vaultName?: string;
}) => {
  const navigate = useNavigate();
  
  const handleNavigateHome = () => {
    navigate('/');
  };

  return (
    <VaultErrorBoundaryClass 
      vaultId={vaultId} 
      vaultName={vaultName}
      onNavigateHome={handleNavigateHome}
    >
      {children}
    </VaultErrorBoundaryClass>
  );
};